import { jwtDecode } from "jwt-decode";

// SAME STORAGE KEYS AS `LOCAL_STORAGE_KEY` IN THE ANGULAR `auth.service.ts`, SO A
// TOKEN WRITTEN BY EITHER APP (OR BY A NATIVE SHELL'S `?t=` HANDOFF) IS READ BY BOTH.
export const USER_TOKEN_KEY = "USER_TOKEN";
const USER_PROFILE_KEYS = [
  "USER_FIRST_NAME",
  "USER_LAST_NAME",
  "USER_USERNAME",
  "USER_AD_GUID",
  "USER_AD_SID",
];

export interface TokenClaims {
  userId?: number;
  // COMMA-SEPARATED ROLE IDS, E.G. "3,7,12"
  roles?: string;
  exp?: number;
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(USER_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  try {
    localStorage.setItem(USER_TOKEN_KEY, token);
  } catch {
    // STORAGE UNAVAILABLE (PRIVATE MODE, BLOCKED SITE DATA) - NOTHING TO PERSIST INTO
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(USER_TOKEN_KEY);
    USER_PROFILE_KEYS.forEach((k) => localStorage.removeItem(k));
  } catch {
    // SEE `setToken`
  }
}

export function readClaims(token: string | null = getToken()): TokenClaims | null {
  if (!token) return null;
  try {
    return jwtDecode<TokenClaims>(token);
  } catch {
    return null;
  }
}

// MIRRORS THE ANGULAR `isLoggedIn()`: ONLY AN `exp` IN THE PAST REJECTS A
// DECODABLE TOKEN. A TOKEN WITHOUT `exp` IS ACCEPTED THERE, SO IT IS HERE TOO -
// TIGHTENING THAT IS A BEHAVIOR CHANGE FOR THE GOVERNING DESIGN, NOT THE PILOT.
export function isTokenValid(token: string | null = getToken(), nowMs: number = Date.now()): boolean {
  const claims = readClaims(token);
  if (!claims) return false;
  return !(typeof claims.exp === "number" && claims.exp < nowMs / 1000);
}

export function roleIdsOf(claims: TokenClaims | null): string[] {
  return (claims?.roles ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}
